import { useEffect, useState, useContext } from "react";
import axios from 'axios';

import { AppContext } from '../../context.js';
import { DateToFormat } from "../../utils/datetime.js";

function percent(part, whole) {
  return whole > 0 ? Math.round(part * 100 / whole) : 0;
}

function ProgressBar({ value, total }) {
  return (
    <div className="mt-2" style={{ height: 6, borderRadius: 3, background: "#E3E8F0", overflow: "hidden" }}>
      <div style={{ width: percent(value, total) + "%", height: "100%", background: "#3062D4" }} />
    </div>
  );
}

// Monthly touchpoint and claim metrics. Navigators get their own numbers first,
// then org totals; admins also get a row per navigator (the server decides which).
function NavigatorMetrics() {
  const context = useContext(AppContext);

  const [month] = useState(() => DateToFormat(new Date(), "YYYY-MM"));
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    let u = context.addSpiner();
    axios.post('/api/admin/analytics/navigators/metrics', {
      month: month,
    }).then((response) => {
      setMetrics(response.data);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, [month]);

  if (!metrics) {
    return null;
  }

  const { org, me, navigators } = metrics;
  const monthLabel = new Date(month + "-01T00:00:00").toLocaleString("en-US", { month: "long", year: "numeric" });
  const showMine = !context.isAdmin || me.assigned > 0;

  return (
    <>
      <div className="row mb-4">
        <div className="col-xs-12">
          <div className="d-flex justify-content-start align-items-stretch">
            {showMine && (
              <div className="card blue w-25 me-2">
                <div className="card-content sm">
                  <div className="typo-strong-150">Your touchpoints</div>
                  <div className="typo-strong-300">{me.logged} / {me.assigned}</div>
                  <div className="typo-normal-150">{monthLabel} · {percent(me.logged, me.assigned)}%</div>
                  <ProgressBar value={me.logged} total={me.assigned} />
                </div>
              </div>
            )}
            <div className="card w-25 me-2">
              <div className="card-content sm">
                <div className="typo-strong-150">Organization touchpoints</div>
                <div className="typo-strong-300">{org.touchpointsLogged} / {org.touchpointsRequired}</div>
                <div className="typo-normal-150">{monthLabel} · {percent(org.touchpointsLogged, org.touchpointsRequired)}%</div>
                <ProgressBar value={org.touchpointsLogged} total={org.touchpointsRequired} />
              </div>
            </div>
            <div className="card w-25 me-2">
              <div className="card-content sm">
                <div className="typo-strong-150">Claims submitted</div>
                <div className="typo-strong-300">{org.claimsSubmitted}</div>
                <div className="typo-normal-150">{monthLabel} · {org.claimsPercent}% complete</div>
                <ProgressBar value={org.claimsPercent} total={100} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {context.isAdmin && navigators.length > 0 && (
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="card">
              <div className="card-header sm">
                <div className="typo-strong-200">Touchpoints by navigator</div>
              </div>
              <div className="card-content sm">
                {navigators.map((row) => {
                  const mine = row.userID !== null && row.userID === me.userID;
                  return (
                    <div key={row.userID || "unassigned"} className={"d-flex justify-content-between align-items-center mb-1 " + (mine ? "card blue" : "")} style={{ padding: "4px 8px" }}>
                      <div className={"w-50 " + (mine ? "typo-strong-150" : "typo-normal-150")}>{row.name}{mine ? " (you)" : ""}</div>
                      <div className="w-25 typo-normal-150 text-end">{row.logged} / {row.assigned}</div>
                      <div className="w-25 typo-normal-150 text-end">{percent(row.logged, row.assigned)}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default NavigatorMetrics;
